import React from 'react';
import { MarketingPage, MarketingContent } from '../components/MarketingPage';

const content: MarketingContent = {
  seo: {
    title: 'Transfer Files Between Phone and PC — Instantly, Over Wi-Fi',
    description:
      'Move photos, videos, and documents between your phone and computer with no cables, no cloud uploads, and no emailing yourself. Works iPhone or Android ⇄ Windows, Mac, or Linux.',
    path: '/transfer-files-phone-to-pc',
  },
  eyebrow: 'Phone ⇄ computer',
  h1: <>Transfer files between your phone and PC — <span className="em">instantly, over Wi-Fi.</span></>,
  lead:
    'Photos, videos, PDFs, whole folders — move them between your phone and your computer without a cable, without uploading to a cloud drive, and without emailing files to yourself.',
  sections: [
    {
      h2: 'The usual ways are slow, clumsy, or leaky',
      paras: [
        'A USB cable means finding the right cable, unlocking the phone, and clicking through “trust this computer.” Cloud drives upload your file to a company’s servers and make you download it again on the other side — twice the wait, and a copy of your data left with someone else. Emailing yourself caps out on attachment size and clutters your inbox.',
        'Local Area Cloud skips all of it: the file goes straight from one of your devices to the other, over your own network.',
      ],
    },
    {
      h2: 'How it works',
      paras: [
        'Install the app on your phone and your computer and sign in to the same free account. They find each other on your Wi-Fi, you pick the destination device, choose your files, and they transfer directly at local-network speed — nothing is uploaded to us.',
      ],
      bullets: [
        <><b>Both directions:</b> phone → PC to offload photos, or PC → phone to load up files.</>,
        <><b>iPhone and Android:</b> works with either, to Windows, Mac, or Linux.</>,
        <><b>Big files welcome:</b> 4K videos and large folders, not just small attachments.</>,
        <><b>Nothing left behind:</b> no copy of your file sits on a third-party server.</>,
      ],
    },
    {
      h2: 'Great for the everyday stuff',
      bullets: [
        'Clearing a full camera roll onto your desktop at the end of a trip.',
        'Getting a video off your phone to edit on a real screen.',
        'Sending a signed PDF or scan from your phone to your work computer.',
        'Loading movies or documents onto your phone before a flight.',
      ],
    },
    {
      h2: 'Set it up in three steps',
      bullets: [
        <><b>1. Download</b> Local Area Cloud on your phone and your computer.</>,
        <><b>2. Sign in</b> to the same free account on both.</>,
        <><b>3. Send</b> — pick the other device, choose files, done.</>,
      ],
    },
  ],
  faqs: [
    {
      q: 'Does it work with an iPhone?',
      a: 'Yes — iPhone and Android both work, in either direction, to Windows, Mac, or Linux computers.',
    },
    {
      q: 'Do I need a cable?',
      a: 'No. Transfers happen wirelessly over your local Wi-Fi network. No cable, no “trust this computer” prompts.',
    },
    {
      q: 'Are my files uploaded to the internet?',
      a: 'No. Files move directly between your devices on your network. Our servers only help your devices find each other and never receive your file contents.',
    },
    {
      q: 'Is it free?',
      a: 'Yes, free for up to two devices with all features. Pro adds unlimited devices and secure remote transfers across networks.',
    },
    {
      q: 'How big a file can I send?',
      a: 'There’s no email-style attachment limit — send large videos and entire folders between your devices.',
    },
  ],
  related: [
    { label: 'AirDrop for Windows', to: '/airdrop-for-windows', note: 'iPhone ⇄ PC, finally' },
    { label: 'Local network file sharing', to: '/local-network-file-sharing', note: 'How LAN transfer works' },
    { label: 'File sharing without the cloud', to: '/file-sharing-without-the-cloud', note: 'Keep files off servers' },
  ],
  ctaBand: {
    h2: 'Your phone and PC, finally on speaking terms.',
    p: 'No cables, no cloud, no emailing yourself. Free for up to two devices.',
  },
};

export const TransferFilesPhoneToPc: React.FC = () => <MarketingPage content={content} />;
export default TransferFilesPhoneToPc;
